import { Group, InputDecimal } from '@/components/_common';
import ActionIcon from '@/components/_common/ActionIcon';
import {
  KitCompany,
  KitItemCreate,
  KitItemType,
} from '@/core/domain/operational/Kits/Kits.types';
import { useKitItemsContextEdit } from '@/pages/OperationalPages/KitsPage/KitDetailPage';
import { useKitItemsContext } from '@/pages/OperationalPages/KitsPage/KitsCreatePage';
import Authorities from '@/types/Authority';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import { quantityFormatter } from '@/utils/number';
import formatterPrice from '@/utils/number/formatterPrice';
import { Popover, Table } from 'antd';
import { ColumnsType } from 'antd/es/table/interface';
import { Edit } from 'react-feather';

interface Props {
  companies: KitCompany[];
  isEdit?: boolean;
  kitItem: KitItemCreate;
}

const KitItemCompaniesList: React.FC<Props> = ({
  isEdit = false,
  companies,
  kitItem,
}) => {
  const { items, setItems } = isEdit
    ? useKitItemsContextEdit()
    : useKitItemsContext();

  function changeFieldCompany(
    companyKey: [number, keyof KitCompany],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: any,
  ) {
    setItems((state) =>
      state.map((item) => {
        if (item.itemId === kitItem.itemId) {
          return {
            ...item,
            companies: item.companies.map((company, cIndex) => {
              if (companyKey[0] === cIndex) {
                return { ...company, [companyKey[1]]: value };
              }
              return company;
            }),
          };
        }
        return item;
      }),
    );
  }

  function redirectToItem(companyId: number) {
    if (kitItem.itemType === KitItemType.part) {
      window.open(
        `/app/operational/inventory/parts/${kitItem.itemId}/stock?companyId=${companyId}`,
        '_blank',
      );
    } else {
      window.open(
        `/app/operational/tasks/${kitItem.itemId}/general?companyId=${companyId}`,
        '_blank',
      );
    }
  }

  const columns: ColumnsType<KitCompany> = [
    {
      dataIndex: 'name',
      title: 'Empresa',
      width: 100,
      ellipsis: true,
    },
    {
      dataIndex: 'price',
      title: 'Preço de Venda',
      width: 50,
      render: (value, item) => (
        <Group gap={16}>
          <Popover content="Alterar preço">
            <ActionIcon onClick={() => redirectToItem(item.companyId)}>
              <Edit size={18} />
            </ActionIcon>
          </Popover>

          <span>{formatterPrice(value || 0)}</span>
        </Group>
      ),
    },
    {
      dataIndex: 'increaseDecrease',
      title: 'Acréscimo/Decréscimo (%)',
      width: 50,
      render: (value, _, index) => {
        const increaseDecreasePather = items?.find(
          (item) =>
            item.itemId === kitItem.itemId &&
            typeof item.increaseDecrease === 'number',
        )?.increaseDecrease;

        return (
          <InputDecimal
            value={increaseDecreasePather || value}
            onChange={(newValue) =>
              changeFieldCompany([index, 'increaseDecrease'], newValue)
            }
            disabled={
              !!increaseDecreasePather ||
              !hasPermissions([Authorities.KITS_CHANGE_PRICES])
            }
          />
        );
      },
    },
    {
      dataIndex: 'total',
      title: 'Total',
      width: 50,
      render: (_, row) => {
        const total =
          row.price + (row.price * (row.increaseDecrease ?? 0)) / 100;

        return `R$ ${quantityFormatter(total)}`;
      },
    },
  ];

  return (
    <Table
      pagination={false}
      dataSource={companies}
      scroll={{ x: true }}
      columns={columns}
    />
  );
};

export default KitItemCompaniesList;
